// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@vanmeter/App/Components/Common/XngZone";
import { isCheckoutPage } from "@vanmeter/App/Store/Selectors/CheckoutSelector";
import { xIsAuthenticated } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import React, { FC } from "react";
import { shallowEqual } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Changes
    mainContainer?: InjectableCss;
    topSection?: InjectableCss;
    bottomSection?: InjectableCss;
    middleSection?: InjectableCss;
    leftSection?: InjectableCss;
    websiteInfo?: InjectableCss;
    logoMessageSection?: InjectableCss;
    mobileDownloadSection?: InjectableCss;
    linklistWrapper?: InjectableCss;
    socialImageLinksSection?: InjectableCss;
    copyRightTermOfServiceSection?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    // XNG-Changes
    const { isAuthenticated, xIsCheckoutPage } = useAppSelector(state => {
        return {
            isAuthenticated: xIsAuthenticated(state),
            xIsCheckoutPage: isCheckoutPage(state),
        };
    }, shallowEqual);

    // XNG-Changes:change structure
    return (
        <StyledWrapper {...styles.mainContainer}>
            {!isAuthenticated && !xIsCheckoutPage && (
                <StyledWrapper {...styles.topSection}>
                    <XngZone zoneName="Subscribe" contentId={id} hint="Subscribe" />
                </StyledWrapper>
            )}
            {!xIsCheckoutPage && (
                <StyledWrapper {...styles.middleSection} {...{ isAuthenticated }}>
                    <StyledWrapper {...styles.leftSection}>
                        <StyledWrapper {...styles.logoMessageSection}>
                            <XngZone zoneName="logo" contentId={id} hint="logo" />
                            <XngZone zoneName="companyMessageContent" contentId={id} hint="company message" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.websiteInfo}>
                            <XngZone zoneName="websiteInfo" contentId={id} hint="website information" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.mobileDownloadSection}>
                            <XngZone zoneName="downloadsLinkImages" contentId={id} hint="mobile download links" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.linklistWrapper}>
                        <XngZone zoneName="LinkList1" contentId={id} hint="Link List 1" />
                        <XngZone zoneName="LinkList2" contentId={id} hint="Link List 2" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.linklistWrapper}>
                        <XngZone zoneName="LinkList3" contentId={id} hint="Link List 3" />
                        <XngZone zoneName="LinkList4" contentId={id} hint="Link List 4" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.linklistWrapper}>
                        <XngZone zoneName="LinkList5" contentId={id} hint="Link List 5" />
                        <XngZone zoneName="LinkList6" contentId={id} hint="Link List 6" />
                    </StyledWrapper>
                    <XngZone zoneName="LinkList7" contentId={id} hint="Link List 7" />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.bottomSection}>
                {!xIsCheckoutPage && (
                    <StyledWrapper {...styles.socialImageLinksSection}>
                        <XngZone zoneName="SocialLinks" contentId={id} hint="Social Links" />
                        <XngZone zoneName="ImagesLinks" contentId={id} hint="Images Links" />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.copyRightTermOfServiceSection}>
                    <XngZone zoneName="CopyRights" hint="Copy Rights" contentId={id} />
                    <XngZone zoneName="TermsOfService" hint="Terms Of Service" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
        // <GridContainer {...styles.widgetGridContainer}>
        //     <GridItem {...styles.logoGridItem}>
        //         <StyledWrapper {...styles.logoWrapper}>
        //             <Zone zoneName="Logo" contentId={id} />
        //         </StyledWrapper>
        //     </GridItem>
        //     <GridItem {...styles.linkList1GridItem}>
        //         <Zone zoneName="LinkList1" contentId={id} />
        //     </GridItem>
        //     <GridItem {...styles.linkList2GridItem}>
        //         <Zone zoneName="LinkList2" contentId={id} />
        //     </GridItem>
        //     <GridItem {...styles.linkList3GridItem}>
        //         <Zone zoneName="LinkList3" contentId={id} />
        //     </GridItem>
        //     <GridItem {...styles.linkList4GridItem}>
        //         <Zone zoneName="LinkList4" contentId={id} />
        //     </GridItem>
        //     <GridItem {...styles.subscribeGridItem}>
        //         <Zone zoneName="Subscribe" contentId={id} />
        //     </GridItem>
        //     <GridItem {...styles.socialLinksGridItem}>
        //         <Zone zoneName="SocialLinks" contentId={id} />
        //     </GridItem>
        //     <GridItem {...styles.siteMapLinksGridItem}>
        //         <Zone zoneName="SiteMapLinks" contentId={id} />
        //     </GridItem>
        //     <GridItem {...styles.richContentGridItem}>
        //         <Zone zoneName="RichContent" contentId={id} />
        //     </GridItem>
        // </GridContainer>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
